<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import QRCode from 'qrcode';
import SSOSitesCard from "@/components/sso/SSOSitesCard.vue";
import {
  authMethods,
  authComputed,
  notificationMethods
} from "@/state/helpers";

export default {
  name: 'TwoFactorSettings',
  components: {
    Layout,
    PageHeader,
    SSOSitesCard
  },
  data() {
    return {
      pageHeader: {
        title: "Autenticación en dos pasos",
        breadcrumbs: [
          {
            text: "Inicio",
            href: "/"
          },
          {
            text: "Autenticación 2FA",
            active: true
          }
        ]
      },
      // 2FA Status and data
      status: null,
      loading: false,
      
      // Setup process state
      setupMode: false,
      setupStep: 1,
      qrCodeUrl: '',
      qrCodeDataUrl: '',
      secretKey: '',
      backupCodes: [],
      
      // Form data
      confirmCode: '',
      disablePassword: '',
      regeneratePassword: '',
      
      // UI state
      showBackupCodes: false,
      showRegenerateForm: false,
      showDisableForm: false,
      
      // Validation
      submitted: false
    }
  },
  computed: {
    ...authComputed,
    
    // Check if 2FA is enabled
    is2FAEnabled() {
      return this.status?.is_enabled || false;
    },
    
    // Check if 2FA is required by admin
    is2FARequired() {
      return this.status?.is_required || false;
    },
    
    // Get formatted last used date
    lastUsedFormatted() {
      if (!this.status?.last_used_at) return 'Nunca';
      return new Date(this.status.last_used_at).toLocaleString('es-ES');
    },
    
    // Get recovery codes count
    recoveryCodesCount() {
      return this.status?.recovery_codes_count || 0;
    },
    
    // Check if user can disable 2FA (not required by admin)
    canDisable2FA() {
      return this.is2FAEnabled && !this.is2FARequired;
    }
  },
  async mounted() {
    await this.loadStatus();
  },
  methods: {
    ...authMethods,
    ...notificationMethods,
    
    // Load current 2FA status
    async loadStatus() {
      try {
        this.loading = true;
        this.status = await this.get2FAStatus();
      } catch (error) {
        console.error('Error loading 2FA status:', error);
        this.error('Error al cargar el estado de 2FA');
      } finally {
        this.loading = false;
      }
    },
    
    // Start 2FA setup process
    async enableTwoFactor() {
      try {
        this.loading = true;
        const response = await this.enable2FA();

        this.qrCodeUrl = response.qr_code_url;
        this.secretKey = response.secret_key;
        this.backupCodes = response.backup_codes;
        
        // Generate QR code from URL
        await this.generateQRCode();
        
        this.setupMode = true;
        this.setupStep = 1;
        
      } catch (error) {
        console.error('Error enabling 2FA:', error);
        this.error('Error al habilitar 2FA');
      } finally {
        this.loading = false;
      }
    },
    
    // Confirm 2FA setup with verification code
    async confirmTwoFactor() {
      if (!this.confirmCode || this.confirmCode.length !== 6) {
        this.error('Ingresa un código de 6 dígitos');
        return;
      }
      
      try {
        this.loading = true;
        await this.confirm2FA({ code: this.confirmCode });
        
        this.success('¡2FA habilitado exitosamente!');
        this.setupMode = false;
        this.resetSetupData();
        await this.loadStatus();
        
      } catch (error) {
        console.error('Error confirming 2FA:', error);
        this.error('Código inválido. Verifica tu aplicación autenticadora.');
      } finally {
        this.loading = false;
      }
    },
    
    // Disable 2FA
    async disableTwoFactor() {
      if (!this.disablePassword) {
        this.error('Ingresa tu contraseña para deshabilitar 2FA');
        return;
      }
      
      try {
        this.loading = true;
        await this.disable2FA({ password: this.disablePassword });
        
        this.success('2FA deshabilitado exitosamente');
        this.showDisableForm = false;
        this.disablePassword = '';
        await this.loadStatus();
        
      } catch (error) {
        console.error('Error disabling 2FA:', error);
        this.error('Contraseña incorrecta o error al deshabilitar 2FA');
      } finally {
        this.loading = false;
      }
    },
    
    // Regenerate recovery codes
    async regenerateRecoveryCodes() {
      if (!this.regeneratePassword) {
        this.error('Ingresa tu contraseña para regenerar códigos');
        return;
      }
      
      try {
        this.loading = true;
        const response = await this.$store.dispatch('auth/regenerateRecoveryCodes', { password: this.regeneratePassword });
        
        this.backupCodes = response.recovery_codes;
        this.showBackupCodes = true;
        this.showRegenerateForm = false;
        this.regeneratePassword = '';
        
        this.success('Códigos de recuperación regenerados exitosamente');
        await this.loadStatus();
        
      } catch (error) {
        console.error('Error regenerating recovery codes:', error);
        this.error('Error al regenerar códigos de recuperación');
      } finally {
        this.loading = false;
      }
    },
    
    // Cancel setup process
    cancelSetup() {
      this.setupMode = false;
      this.resetSetupData();
    },
    
    // Generate QR code from URL
    async generateQRCode() {
      if (!this.qrCodeUrl) return;
      
      try {
        // Generate QR code as data URL
        this.qrCodeDataUrl = await QRCode.toDataURL(this.qrCodeUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
      } catch (error) {
        console.error('Error generating QR code:', error);
        this.error('Error al generar el código QR');
      }
    },
    
    // Reset setup data
    resetSetupData() {
      this.setupStep = 1;
      this.qrCodeUrl = '';
      this.qrCodeDataUrl = '';
      this.secretKey = '';
      this.backupCodes = [];
      this.confirmCode = '';
    },
    
    // Continue to step 2
    continueToStep2() {
      if (this.setupStep === 1) {
        this.setupStep = 2;
      }
    },
    
    // Go back to step 1
    backToStep1() {
      if (this.setupStep === 2) {
        this.setupStep = 1;
      }
    },
    
    // Copy backup codes to clipboard
    async copyBackupCodes() {
      const codesText = this.backupCodes.join('\n');
      
      try {
        await navigator.clipboard.writeText(codesText);
        this.success('Códigos copiados al portapapeles');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        this.error('No se pudieron copiar los códigos');
      }
    },
    
    // Copy secret key to clipboard
    async copySecretKey() {
      try {
        await navigator.clipboard.writeText(this.secretKey);
        this.success('Clave secreta copiada al portapapeles');
      } catch (error) {
        console.error('Error copying secret key:', error);
        this.error('No se pudo copiar la clave secreta');
      }
    },
    
    // Copy QR URL to clipboard
    async copyQRUrl() {
      try {
        await navigator.clipboard.writeText(this.qrCodeUrl);
        this.success('URL del código QR copiada al portapapeles');
      } catch (error) {
        console.error('Error copying QR URL:', error);
        this.error('No se pudo copiar la URL del código QR');
      }
    },
    
    // Handle code input (only numbers, max 6 digits)
    onCodeInput(event) {
      const value = event.target.value.replace(/\D/g, '').slice(0, 6);
      this.confirmCode = value;
    },
    
    // Toggle forms
    toggleDisableForm() {
      this.showDisableForm = !this.showDisableForm;
      this.disablePassword = '';
    },
    
    toggleRegenerateForm() {
      this.showRegenerateForm = !this.showRegenerateForm;
      this.regeneratePassword = '';
    },
    
    toggleBackupCodes() {
      this.showBackupCodes = !this.showBackupCodes;
    }
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="pageHeader?.title" :items="pageHeader?.breadcrumbs" />
    
    <div class="row justify-content-center">
      <div class="col-xl-8 col-lg-10">
        <div class="card">
          <div class="card-body">
            <!-- Loading State -->
            <div v-if="loading && !status" class="two-factor-loading">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Cargando...</span>
              </div>
              <div class="loading-text mt-3">Cargando configuración 2FA...</div>
            </div>

            <!-- Main Content -->
            <div v-else class="two-factor-settings">
              <!-- Header -->
              <div class="text-center mb-4">
                <i class="ri-shield-check-line display-4 text-primary"></i>
                <h4 class="mt-3">Autenticación en dos pasos</h4>
                <p class="text-muted">
                  Agrega una capa extra de seguridad a tu cuenta usando una aplicación autenticadora.
                </p>
              </div>

              <!-- Current Status Card -->
              <div v-if="status" class="two-factor-status-card" :class="{ 
                'enabled': is2FAEnabled, 
                'disabled': !is2FAEnabled && !is2FARequired,
                'required': is2FARequired && !is2FAEnabled 
              }">
                <div class="d-flex align-items-center">
                  <div class="status-icon mr-3">
                    <i v-if="is2FAEnabled" class="ri-shield-check-fill font-size-24"></i>
                    <i v-else-if="is2FARequired" class="ri-shield-star-line font-size-24"></i>
                    <i v-else class="ri-shield-cross-line font-size-24"></i>
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mb-1">
                      <span v-if="is2FAEnabled">✅ 2FA Habilitado</span>
                      <span v-else-if="is2FARequired">⏳ 2FA Requerido</span>
                      <span v-else>⚠️ 2FA Deshabilitado</span>
                    </h6>
                    <div class="small">
                      <div v-if="is2FAEnabled">
                        <strong>Último uso:</strong> {{ lastUsedFormatted }}
                      </div>
                      <div v-if="is2FAEnabled">
                        <strong>Códigos de recuperación:</strong> {{ recoveryCodesCount }}
                      </div>
                      <div v-if="is2FARequired && !is2FAEnabled" class="text-warning">
                        <i class="ri-information-line mr-1"></i>
                        Tu administrador requiere que habilites 2FA
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Enable 2FA Button -->
              <div v-if="!is2FAEnabled && !setupMode" class="text-center mb-4">
                <button 
                  @click="enableTwoFactor" 
                  class="btn btn-primary btn-lg"
                  :disabled="loading"
                >
                  <i class="ri-shield-check-line mr-2"></i>
                  Habilitar 2FA
                </button>
                <div class="mt-2 text-muted small">
                  Necesitarás una aplicación autenticadora como Google Authenticator o Authy
                </div>
              </div>

              <!-- Setup Process -->
              <div v-if="setupMode" class="two-factor-steps">
                <!-- Step Indicator -->
                <div class="step-indicator">
                  <div class="step-item" :class="{ active: setupStep === 1, completed: setupStep > 1 }">
                    <div class="step-number">1</div>
                    <span>Escanear QR</span>
                  </div>
                  <div class="step-item" :class="{ active: setupStep === 2 }">
                    <div class="step-number">2</div>
                    <span>Verificar</span>
                  </div>
                </div>

                <!-- Step 1: QR Code -->
                <div v-if="setupStep === 1" class="step active">
                  <div class="text-center">
                    <h5 class="mb-3">Paso 1: Escanea el código QR</h5>
                    <p class="text-muted mb-4">
                      Usa tu aplicación autenticadora para escanear este código QR:
                    </p>

                    <!-- QR Code -->
                    <div class="qr-code-container">
                      <div v-if="!qrCodeDataUrl && qrCodeUrl" class="text-center p-4">
                        <div class="spinner-border text-primary mb-2" role="status">
                          <span class="sr-only">Generando QR...</span>
                        </div>
                        <div class="small text-muted">Generando código QR...</div>
                      </div>
                      <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="QR Code para 2FA" class="qr-code-image" />
                    </div>

                    <!-- Manual Entry -->
                    <div class="mt-4">
                      <p class="mb-2"><strong>O ingresa manualmente:</strong></p>
                      <div class="secret-key-display" @click="copySecretKey">
                        {{ secretKey }}
                        <i class="ri-file-copy-line ml-2 text-primary"></i>
                      </div>
                      <div class="two-factor-help">
                        <i class="ri-information-line help-icon"></i>
                        Haz clic para copiar la clave secreta
                      </div>
                    </div>

                    <!-- Backup Codes -->
                    <div class="mt-4">
                      <h6 class="mb-3">Códigos de recuperación:</h6>
                      <p class="text-muted small mb-3">
                        Guarda estos códigos en un lugar seguro. Puedes usarlos para acceder si pierdes tu dispositivo.
                      </p>
                      <ul class="recovery-codes">
                        <li v-for="code in backupCodes" :key="code">{{ code }}</li>
                      </ul>
                      <button 
                        @click="copyBackupCodes" 
                        class="btn btn-outline-secondary btn-sm"
                      >
                        <i class="ri-file-copy-line mr-1"></i>
                        Copiar códigos
                      </button>
                    </div>

                    <!-- Actions -->
                    <div class="mt-4 d-flex justify-content-between">
                      <button 
                        @click="cancelSetup" 
                        class="btn btn-secondary"
                        :disabled="loading"
                      >
                        Cancelar
                      </button>
                      <button 
                        @click="continueToStep2" 
                        class="btn btn-primary"
                      >
                        Continuar
                        <i class="ri-arrow-right-line ml-1"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 2: Verification -->
                <div v-if="setupStep === 2" class="step active">
                  <div class="text-center">
                    <h5 class="mb-3">Paso 2: Confirma con un código</h5>
                    <p class="text-muted mb-4">
                      Ingresa el código de 6 dígitos de tu aplicación autenticadora:
                    </p>

                    <div class="form-group">
                      <input 
                        v-model="confirmCode"
                        @input="onCodeInput"
                        type="text" 
                        maxlength="6"
                        placeholder="123456"
                        class="form-control form-control-lg text-center two-factor-code-input"
                        style="max-width: 200px; margin: 0 auto;"
                      />
                    </div>

                    <!-- Actions -->
                    <div class="mt-4 d-flex justify-content-between">
                      <button 
                        @click="backToStep1" 
                        class="btn btn-secondary"
                        :disabled="loading"
                      >
                        <i class="ri-arrow-left-line mr-1"></i>
                        Volver
                      </button>
                      <button 
                        @click="confirmTwoFactor" 
                        class="btn btn-success"
                        :disabled="loading || confirmCode.length !== 6"
                      >
                        <span v-if="loading">
                          <i class="mdi mdi-spin mdi-loading mr-1"></i>
                          Verificando...
                        </span>
                        <span v-else>
                          <i class="ri-check-line mr-1"></i>
                          Confirmar y activar
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Management Options (when 2FA is enabled) -->
              <div v-if="is2FAEnabled && !setupMode" class="enabled-options">
                <div class="row">
                  <!-- Regenerate Recovery Codes -->
                  <div class="col-md-6 mb-3">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="ri-key-line font-size-24 text-warning mb-2"></i>
                        <h6>Códigos de recuperación</h6>
                        <p class="text-muted small">
                          Regenera tus códigos de recuperación si los has perdido o usado.
                        </p>
                        <button 
                          @click="toggleRegenerateForm" 
                          class="btn btn-warning btn-sm"
                        >
                          Regenerar códigos
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Disable 2FA -->
                  <div v-if="canDisable2FA" class="col-md-6 mb-3">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="ri-shield-cross-line font-size-24 text-danger mb-2"></i>
                        <h6>Deshabilitar 2FA</h6>
                        <p class="text-muted small">
                          Desactiva la autenticación en dos pasos para tu cuenta.
                        </p>
                        <button 
                          @click="toggleDisableForm" 
                          class="btn btn-danger btn-sm"
                        >
                          Deshabilitar 2FA
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Cannot Disable (Required by Admin) -->
                  <div v-else class="col-md-6 mb-3">
                    <div class="card h-100">
                      <div class="card-body text-center">
                        <i class="ri-lock-line font-size-24 text-secondary mb-2"></i>
                        <h6>2FA Requerido</h6>
                        <p class="text-muted small">
                          Tu administrador requiere que mantengas 2FA habilitado.
                        </p>
                        <button class="btn btn-secondary btn-sm" disabled>
                          No se puede deshabilitar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Regenerate Recovery Codes Form -->
                <div v-if="showRegenerateForm" class="mt-4">
                  <div class="card border-warning">
                    <div class="card-body">
                      <h6 class="card-title text-warning">
                        <i class="ri-key-line mr-2"></i>
                        Regenerar códigos de recuperación
                      </h6>
                      <p class="text-muted small mb-3">
                        Ingresa tu contraseña para generar nuevos códigos de recuperación. Los códigos anteriores dejarán de funcionar.
                      </p>
                      <div class="form-group">
                        <input 
                          v-model="regeneratePassword"
                          type="password" 
                          placeholder="Tu contraseña"
                          class="form-control"
                        />
                      </div>
                      <div class="d-flex justify-content-end gap-2">
                        <button 
                          @click="toggleRegenerateForm" 
                          class="btn btn-secondary btn-sm"
                        >
                          Cancelar
                        </button>
                        <button 
                          @click="regenerateRecoveryCodes" 
                          class="btn btn-warning btn-sm"
                          :disabled="loading || !regeneratePassword"
                        >
                          <span v-if="loading">
                            <i class="mdi mdi-spin mdi-loading mr-1"></i>
                            Regenerando...
                          </span>
                          <span v-else>Regenerar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Disable 2FA Form -->
                <div v-if="showDisableForm" class="mt-4">
                  <div class="card border-danger">
                    <div class="card-body">
                      <h6 class="card-title text-danger">
                        <i class="ri-shield-cross-line mr-2"></i>
                        Deshabilitar autenticación en dos pasos
                      </h6>
                      <p class="text-muted small mb-3">
                        Ingresa tu contraseña para deshabilitar 2FA. Tu cuenta será menos segura.
                      </p>
                      <div class="form-group">
                        <input 
                          v-model="disablePassword"
                          type="password" 
                          placeholder="Tu contraseña"
                          class="form-control"
                        />
                      </div>
                      <div class="d-flex justify-content-end gap-2">
                        <button 
                          @click="toggleDisableForm" 
                          class="btn btn-secondary btn-sm"
                        >
                          Cancelar
                        </button>
                        <button 
                          @click="disableTwoFactor" 
                          class="btn btn-danger btn-sm"
                          :disabled="loading || !disablePassword"
                        >
                          <span v-if="loading">
                            <i class="mdi mdi-spin mdi-loading mr-1"></i>
                            Deshabilitando...
                          </span>
                          <span v-else>Deshabilitar 2FA</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Show Backup Codes -->
                <div v-if="showBackupCodes && backupCodes.length" class="mt-4">
                  <div class="card border-success">
                    <div class="card-body">
                      <h6 class="card-title text-success">
                        <i class="ri-key-line mr-2"></i>
                        Nuevos códigos de recuperación
                      </h6>
                      <p class="text-muted small mb-3">
                        Guarda estos códigos en un lugar seguro. No se mostrarán de nuevo.
                      </p>
                      <ul class="recovery-codes">
                        <li v-for="code in backupCodes" :key="code">{{ code }}</li>
                      </ul>
                      <div class="d-flex justify-content-between align-items-center">
                        <button 
                          @click="copyBackupCodes" 
                          class="btn btn-outline-success btn-sm"
                        >
                          <i class="ri-file-copy-line mr-1"></i>
                          Copiar códigos
                        </button>
                        <button 
                          @click="toggleBackupCodes" 
                          class="btn btn-success btn-sm"
                        >
                          Entendido
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Recommended Apps -->
              <div class="mt-5 pt-4 border-top">
                <h6 class="text-muted mb-3">Aplicaciones recomendadas:</h6>
                <div class="row text-center">
                  <div class="col-4">
                    <div class="p-2">
                      <i class="ri-smartphone-line font-size-18 text-primary mb-1"></i>
                      <div class="small">Google<br>Authenticator</div>
                    </div>
                  </div>
                  <div class="col-4">
                    <div class="p-2">
                      <i class="ri-shield-keyhole-line font-size-18 text-success mb-1"></i>
                      <div class="small">Authy</div>
                    </div>
                  </div>
                  <div class="col-4">
                    <div class="p-2">
                      <i class="ri-smartphone-line font-size-18 text-info mb-1"></i>
                      <div class="small">Microsoft<br>Authenticator</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SSO Sites Card -->
    <div class="row justify-content-center mt-4">
      <div class="col-xl-8 col-lg-10">
        <SSOSitesCard 
          variant="compact"
          title="Explora nuestro ecosistema"
          description="Descubre todos los sitios que forman parte de nuestra red"
          :show-footer="true"
        />
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}

.step {
  animation: fadeInUp 0.3s ease-out;
}
</style> 